import { useMemo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { prettyFloatMoney } from '@/helpers/utils';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { ERROR_CODES } from '@/helpers/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { Button } from '@scramble/ui';
import IconInfo from '@scramble/design-system/icons/info-circle.svg';
import s from './index.module.scss';

const id = MODAL_IDS.brands_for_investment_status_modal;

export const BrandsForInvestmentStatusModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({ error, onAddBrands }) => {
  const { closeModal } = useModal();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation('modal');

  const data = useMemo(() => {
    if (error?.code === ERROR_CODES.brand_capacity_full) {
      let text;
      const groupA = !!error?.data?.conservative;
      const groupB = !!error?.data?.moderate;

      if (groupA)
        text = (
          <Trans
            ns="modal"
            i18nKey="brands_for_inv_status.group_capacity"
            values={{
              amount: prettyFloatMoney(error?.data?.conservative),
              group: t('brands_for_inv_status.group_a'),
            }}
            components={{ Span: <span className="f-small-accent" /> }}
          />
        );

      if (groupB)
        text = (
          <Trans
            ns="modal"
            i18nKey="brands_for_inv_status.group_capacity"
            values={{
              amount: prettyFloatMoney(error?.data?.moderate),
              group: t('brands_for_inv_status.group_b'),
            }}
            components={{ Span: <span className="f-small-accent" /> }}
          />
        );

      if (groupA && groupB)
        text = (
          <Trans
            ns="modal"
            i18nKey="brands_for_inv_status.group_a_b_capacity"
            components={{ Span: <span className="f-small-accent" /> }}
            values={{
              conservative: prettyFloatMoney(error?.data?.conservative),
              moderate: prettyFloatMoney(error?.data?.moderate),
            }}
          />
        );

      return {
        title: t('brands_for_inv_status.insufficient_capacity'),
        text,
        actionLabel: t('brands_for_inv_status.add_brands'),
        action: onAddBrands,
      };
    }

    if (error?.code === ERROR_CODES.investment_limit_reached) {
      const isGroupB = !!error?.data?.moderate;
      const amount = isGroupB ? error?.data?.moderate : error?.data?.conservative;
      const group = isGroupB
        ? t('brands_for_inv_status.group_b')
        : t('brands_for_inv_status.group_a');
      return {
        title: t('brands_for_inv_status.limit_reached'),
        text: (
          <Trans
            ns="modal"
            i18nKey="brands_for_inv_status.reduce_allocation"
            components={{ Span: <span className="f-small-accent" /> }}
            values={{
              amount: prettyFloatMoney(amount),
              group,
            }}
          />
        ),
        actionLabel: t('brands_for_inv_status.got_it'),
        action: () => closeModal(id),
      };
    }

    if (error?.code === ERROR_CODES.round_filled)
      return {
        title: t('brands_for_inv_status.round_filled'),
        text: t('brands_for_inv_status.no_additional_investments'),
        actionLabel: t('brands_for_inv_status.got_it'),
        action: () => closeModal(id),
      };

    if (error?.code === ERROR_CODES.brand_unavailable)
      return {
        title: t('brands_for_inv_status.brand_unavailable'),
        text: t('brands_for_inv_status.brand_unavailable_desc'),
        actionLabel: t('brands_for_inv_status.refresh_selection'),
        action: onAddBrands,
      };

    return null;
  }, [error?.code]);

  if (!data) return null;

  const content = (
    <div className={s.modal}>
      <div className={s.content}>
        <div className={s.icon_wrapper}>
          <div className={s.icon}>
            <img src={IconInfo} alt="warn" width={28} height={28} />
          </div>
        </div>
        <h1 className={classnames('f-hero-h5', s.title)}>{data?.title}</h1>
        <p className={classnames('f-small-regular', s.desc)}>{data?.text}</p>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          onClick={data?.action}
        >
          {data?.actionLabel}
        </Button>
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
